import React from 'react';
import {
  Button,
  BUTTON_SIZE_SM,
  BUTTON_VARIANT_TEXT,
  Divider,
  DIVIDER_ORIENTATION_VERTICAL,
  IconButton,
  ICON_BUTTON_SIZE_TB,
  ModeBadge,
  MODE_BADGE_SIZE_SM,
  MODE_BADGE_TONE_PRIMARY,
  MODE_BADGE_TONE_SUCCESS,
  MODE_BADGE_VARIANT_FLAT,
  TOOLBAR_DENSITY_COMPACT,
  TOOLBAR_VARIANT_FLOATING,
  TOOLTIP_PLACEMENT_BOTTOM,
  Toolbar,
} from '@aibuild/ui';
import { useEditorContext } from '../useEditorContext';
import {
  ModeCluster,
  RunsLabel,
  ToolbarWrapper,
} from './CanvasToolbar.styled';
import MaterialIcon from './MaterialIcon';

export interface CanvasToolbarTopProps {
  /** Drives the DRAFT vs PUBLISHED variant. */
  isPublished: boolean;
  /**
   * Open the Duplicate dialog. Reused for both:
   *   - PUBLISHED's "Duplicate & edit" inline button on the mode badge,
   *   - the (future) draft duplicate-process flow if we surface it here.
   */
  onDuplicateProcess?: () => void;
  /**
   * Unpublish a published process back to a draft. Render disabled
   * with an explanatory tooltip when the handler is not provided
   * (no backend endpoint yet -- see ProcessEditorPage).
   */
  onUnpublishProcess?: () => void;
  dataTestId?: string;
}

/**
 * Spacing rationale for the dividers: the prototype's `.ctb` has a 2px
 * inter-button gap inside groups but a much wider 8px+2px sep margin
 * around dividers. The @aibuild/ui Toolbar's `compact` density covers
 * the in-group gap (2px); we pass `dividerInset={8}` to recreate the
 * inter-group breathing room without widening the buttons themselves.
 */
const TOOLBAR_DIVIDER_INSET = 8;

/**
 * Top toolbar tooltips drop BELOW the trigger so they reach into the
 * canvas rather than off-screen above the (already top-anchored)
 * floating shell.
 */
const TOOLTIP_PLACEMENT = TOOLTIP_PLACEMENT_BOTTOM;

/** Pluralisation helper for the Edit-group's selection-count tooltips. */
const pluralizeNode = (count: number): string =>
  count === 1 ? 'node' : 'nodes';

/**
 * Duplicate's tooltip stays node-only — pasted edges aren't a thing in
 * the clipboard pipeline, so the button only makes sense when one or
 * more nodes are selected. Also surfaces Cmd/Ctrl + C / V so users
 * discover the copy / paste-later workflow without a separate
 * affordance.
 */
const buildDuplicateTooltip = (nodeCount: number): string => {
  if (nodeCount === 0) {
    return 'Select one or more nodes to duplicate';
  }
  return `Duplicate ${nodeCount} ${pluralizeNode(nodeCount)} \u2014 \u2318C / Ctrl+C, \u2318V / Ctrl+V`;
};

/**
 * Delete's tooltip is intentionally minimal per the design hand-off
 * (AIB-7081): a literal "Delete" when something is selected — nodes,
 * edges, or both — and a "Select something…" hint when the selection
 * is empty so the user knows why the bin is greyed out.
 */
const buildDeleteTooltip = (selectionCount: number): string => {
  if (selectionCount === 0) {
    return 'Select something to delete';
  }
  return 'Delete';
};

/**
 * Floating top canvas toolbar -- mirrors the handoff prototype's
 * top `.ctb` row (CanvasToolbar.jsx:309-520, styles.css:9419-9732).
 *
 * Two layouts:
 *
 *   DRAFT      [DRAFT] | [Undo Redo] | [Duplicate Delete] | [Clear all]
 *   PUBLISHED  [PUBLISHED + Edit + Unpublish] | [history Runs]
 *
 * The DRAFT Edit group is selection-aware -- Duplicate / Delete are
 * disabled when nothing is selected. Clear-all only renders when at
 * least one node currently has a non-idle run state. The PUBLISHED
 * Runs button is a toggle on the in-canvas Run-history sheet.
 */
const CanvasToolbarTop: React.FC<CanvasToolbarTopProps> = ({
  isPublished,
  onDuplicateProcess,
  onUnpublishProcess,
  dataTestId = 'canvas-toolbar-top',
}) => {
  const ctx = useEditorContext();
  const nodeSelectionCount = ctx.selectedNodeIds.length;
  // Delete operates on nodes AND edges (AIB-7081). Duplicate stays
  // node-only — the clipboard pipeline doesn't paste edges.
  const deleteSelectionCount =
    nodeSelectionCount + ctx.selectedEdgeIds.length;

  if (isPublished) {
    return (
      <ToolbarWrapper $position="top" data-testid={`${dataTestId}__wrapper`}>
        <Toolbar
          variant={TOOLBAR_VARIANT_FLOATING}
          density={TOOLBAR_DENSITY_COMPACT}
          dividerInset={TOOLBAR_DIVIDER_INSET}
          ariaLabel="Canvas tools"
          dataTestId={dataTestId}
        >
          <Toolbar.Group ariaLabel="Mode">
            <ModeCluster>
              <ModeBadge
                variant={MODE_BADGE_VARIANT_FLAT}
                tone={MODE_BADGE_TONE_SUCCESS}
                size={MODE_BADGE_SIZE_SM}
                icon={<MaterialIcon name="rocket_launch" />}
                dataTestId={`${dataTestId}__mode`}
              >
                Published
              </ModeBadge>
              <Divider
                orientation={DIVIDER_ORIENTATION_VERTICAL}
                length={20}
                inset={4}
                dataTestId={`${dataTestId}__mode-sep`}
              />
              <IconButton
                ariaLabel="Duplicate & edit"
                tooltip="Duplicate this process to make changes"
                tooltipPlacement={TOOLTIP_PLACEMENT}
                size={ICON_BUTTON_SIZE_TB}
                icon={<MaterialIcon name="edit" />}
                onClick={onDuplicateProcess}
                disabled={!onDuplicateProcess}
                dataTestId={`${dataTestId}__duplicate-edit`}
              />
              {/* Design uses the `undo` glyph for Unpublish — read as "go
                  back to draft". The published toolbar has no Undo of its
                  own (Edit is read-only) so there's no glyph collision. */}
              <IconButton
                ariaLabel="Unpublish"
                tooltip={
                  onUnpublishProcess
                    ? 'Unpublish back to a draft'
                    : 'Unpublishing is not available yet'
                }
                tooltipPlacement={TOOLTIP_PLACEMENT}
                size={ICON_BUTTON_SIZE_TB}
                icon={<MaterialIcon name="undo" />}
                onClick={onUnpublishProcess}
                disabled={!onUnpublishProcess}
                dataTestId={`${dataTestId}__unpublish`}
              />
            </ModeCluster>
          </Toolbar.Group>
          <Toolbar.Group ariaLabel="Run history">
            <Button
              variant={BUTTON_VARIANT_TEXT}
              size={BUTTON_SIZE_SM}
              leadingIcon={<MaterialIcon name="history" />}
              aria-pressed={ctx.runHistoryOpen}
              tooltip={
                ctx.runHistoryOpen ? 'Hide run history' : 'Show run history'
              }
              tooltipPlacement={TOOLTIP_PLACEMENT}
              onClick={() => ctx.setRunHistoryOpen(!ctx.runHistoryOpen)}
              dataTestId={`${dataTestId}__runs`}
            >
              <RunsLabel>Runs</RunsLabel>
            </Button>
          </Toolbar.Group>
        </Toolbar>
      </ToolbarWrapper>
    );
  }

  // ── DRAFT ────────────────────────────────────────────────────────────────
  return (
    <ToolbarWrapper $position="top" data-testid={`${dataTestId}__wrapper`}>
      <Toolbar
        variant={TOOLBAR_VARIANT_FLOATING}
        density={TOOLBAR_DENSITY_COMPACT}
        dividerInset={TOOLBAR_DIVIDER_INSET}
        ariaLabel="Canvas tools"
        dataTestId={dataTestId}
      >
        <Toolbar.Group ariaLabel="Mode">
          <ModeBadge
            variant={MODE_BADGE_VARIANT_FLAT}
            tone={MODE_BADGE_TONE_PRIMARY}
            size={MODE_BADGE_SIZE_SM}
            dataTestId={`${dataTestId}__mode`}
          >
            Draft
          </ModeBadge>
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Edit history">
          <IconButton
            ariaLabel="Undo"
            tooltip="Undo last change"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="undo" />}
            onClick={ctx.undo}
            disabled={!ctx.canUndo}
            dataTestId={`${dataTestId}__undo`}
          />
          <IconButton
            ariaLabel="Redo"
            tooltip="Redo last change"
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="redo" />}
            onClick={ctx.redo}
            disabled={!ctx.canRedo}
            dataTestId={`${dataTestId}__redo`}
          />
        </Toolbar.Group>
        <Toolbar.Group ariaLabel="Edit">
          <IconButton
            ariaLabel="Duplicate selected"
            tooltip={buildDuplicateTooltip(nodeSelectionCount)}
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="content_copy" />}
            onClick={ctx.duplicateSelectedNodes}
            disabled={nodeSelectionCount === 0}
            dataTestId={`${dataTestId}__duplicate`}
          />
          <IconButton
            ariaLabel="Delete selected"
            tooltip={buildDeleteTooltip(deleteSelectionCount)}
            tooltipPlacement={TOOLTIP_PLACEMENT}
            size={ICON_BUTTON_SIZE_TB}
            icon={<MaterialIcon name="delete" />}
            onClick={ctx.removeSelectedNodes}
            disabled={deleteSelectionCount === 0}
            dataTestId={`${dataTestId}__delete`}
          />
        </Toolbar.Group>
        {ctx.hasNodeRunStates ? (
          <Toolbar.Group ariaLabel="Test run">
            <Button
              variant={BUTTON_VARIANT_TEXT}
              size={BUTTON_SIZE_SM}
              tooltip="Clear all node run statuses"
              tooltipPlacement={TOOLTIP_PLACEMENT}
              onClick={ctx.clearNodeStatuses}
              dataTestId={`${dataTestId}__clear-all`}
            >
              Clear all
            </Button>
          </Toolbar.Group>
        ) : null}
      </Toolbar>
    </ToolbarWrapper>
  );
};

export default CanvasToolbarTop;
